import { sameJson } from "../document/same-json.ts";
import { COLLISION_CELLS_PER_TILE, type LayerDocument, type MapDocument, type WorldDocument } from "../document/types.ts";
import type { CellRun } from "../live/op-types.ts";
import type { LiveRepaint } from "../live/repaint.ts";
import type { CellRect } from "../terrain/match.ts";

/**
 * What a store draws again when it adopts its session's document (ADR-086 decision 26), worked out from the
 * session's repaint for the map in view: a cell patch is a rectangle of tiles on one layer, a terrain patch
 * the vertices its layer's session takes again, and only a change of structure (the map's layers, a layer's
 * terrain, the tileset list, the world's maps) draws the map again, as the same command made in this tab does.
 * A patch of 65,536 cells is one rectangle, never the map.
 */

/**
 * What to draw again.
 *
 * - `all`: the map in view is drawn again whole and its terrain sessions are brought in line.
 * - `reset`: the tileset list changed, so every session and compiled rule stage is dropped first.
 * - `structure`: the map in view's layers or the world's maps changed, so what the view keeps of things that
 *   may be gone is looked at again.
 * - `terrain`: per terrain layer, the vertices whose session cells are taken from the document again.
 * - `rects`: per tile or collision layer, the tiles to paint again.
 * - `faces`: the terrain layers of the map in view whose face height alone changed, whose session runs its
 *   face pass again.
 * - `renamed`: the layers of the map in view whose own fields changed, whose chosen colour is read from their
 *   name again.
 */
export interface LiveRedraw {
  readonly all: boolean;
  readonly reset: boolean;
  readonly structure: boolean;
  readonly terrain: readonly { readonly layerId: string; readonly indices: Uint32Array }[];
  readonly rects: readonly { readonly layerId: string; readonly rect: CellRect }[];
  readonly faces: readonly string[];
  readonly renamed: readonly string[];
}

/**
 * The bounding rectangle of runs of a row-major grid's cells.
 *
 * @param runs The runs, none empty.
 * @param width The grid's width in cells.
 * @returns The rectangle, its far edges exclusive.
 */
function runBounds(runs: readonly CellRun[], width: number): CellRect {
  const rect = { x0: Number.MAX_SAFE_INTEGER, y0: Number.MAX_SAFE_INTEGER, x1: 0, y1: 0 };
  for (const [start, length] of runs) {
    const last = start + length - 1;
    const row = Math.floor(start / width);
    const lastRow = Math.floor(last / width);
    rect.y0 = Math.min(rect.y0, row);
    rect.y1 = Math.max(rect.y1, lastRow + 1);
    rect.x0 = lastRow > row ? 0 : Math.min(rect.x0, start - row * width);
    rect.x1 = lastRow > row ? width : Math.max(rect.x1, last - row * width + 1);
  }
  return runs.length === 0 ? { x0: 0, y0: 0, x1: 0, y1: 0 } : rect;
}

/**
 * Every index runs cover.
 *
 * @param runs The runs.
 * @returns The indices in order.
 */
function indicesOf(runs: readonly CellRun[]): Uint32Array {
  const indices = new Uint32Array(runs.reduce((sum, [, length]) => sum + length, 0));
  let at = 0;
  for (const [start, length] of runs) {
    for (let i = 0; i < length; i += 1) {
      indices[at] = start + i;
      at += 1;
    }
  }
  return indices;
}

/**
 * How a change of a layer's own fields draws: `all` when its kind, or a terrain layer's terrain or variant,
 * changed, so the map is drawn again; `face` when only a terrain layer's face height did; `none` for a name, a
 * group or the `autoName` flag.
 *
 * @param before The layer before, or undefined when the map lacked it.
 * @param after The layer after, or undefined when the map lacks it.
 * @returns What to draw.
 */
function drawOf(before: LayerDocument | undefined, after: LayerDocument | undefined): "all" | "face" | "none" {
  if (before === undefined || after === undefined || before.kind !== after.kind) {
    return "all";
  }
  if (before.kind !== "terrain" || after.kind !== "terrain") {
    return "none";
  }
  if (before.terrain !== after.terrain || before.variant !== after.variant) {
    return "all";
  }
  return before.faceHeight !== after.faceHeight ? "face" : "none";
}

/**
 * What a store draws again for a repaint of its session.
 *
 * @param before The document shown before.
 * @param after The document shown now.
 * @param repaint What the session says differs between them.
 * @param openMap The id of the map in view.
 * @returns What to draw again; parts of maps that are not in view draw nothing.
 */
export function liveRedrawOf(before: WorldDocument, after: WorldDocument, repaint: LiveRepaint, openMap: string): LiveRedraw {
  const map: MapDocument | undefined = after.maps.find((candidate) => candidate.id === openMap);
  const was = before.maps.find((candidate) => candidate.id === openMap);
  let all = false;
  let reset = false;
  let structure = false;
  const terrain: { layerId: string; indices: Uint32Array }[] = [];
  const rects: { layerId: string; rect: CellRect }[] = [];
  const faces: string[] = [];
  const renamed: string[] = [];
  for (const part of repaint) {
    if (part.kind === "world") {
      all = true;
      structure = true;
      reset = reset || before.tilesets.length !== after.tilesets.length || !sameJson(before.tilesets, after.tilesets);
      continue;
    }
    if (part.map !== openMap || map === undefined) {
      continue;
    }
    if (part.kind === "map") {
      all = true;
      structure = true;
      continue;
    }
    if (part.kind === "layer") {
      const draw = drawOf(was?.layers.find((layer) => layer.id === part.layer), map.layers.find((layer) => layer.id === part.layer));
      all = all || draw === "all";
      if (draw === "face") {
        faces.push(part.layer);
      }
      renamed.push(part.layer);
      continue;
    }
    if (part.kind === "items") {
      continue;
    }
    if (part.grid === "corners") {
      terrain.push({ layerId: part.layer, indices: indicesOf(part.cells) });
    } else if (part.grid === "tiles") {
      rects.push({ layerId: part.layer, rect: runBounds(part.cells, map.width) });
    } else if (part.grid === "collision") {
      const fine = runBounds(part.cells, map.width * COLLISION_CELLS_PER_TILE);
      rects.push({ layerId: part.layer, rect: { x0: Math.floor(fine.x0 / COLLISION_CELLS_PER_TILE), y0: Math.floor(fine.y0 / COLLISION_CELLS_PER_TILE), x1: Math.ceil(fine.x1 / COLLISION_CELLS_PER_TILE), y1: Math.ceil(fine.y1 / COLLISION_CELLS_PER_TILE) } });
    }
  }
  return all ? { all, reset, structure, terrain: [], rects: [], faces: [], renamed } : { all, reset, structure, terrain, rects, faces, renamed };
}
